<template>
  <div class="doc-section">
    <div class="section-header">
      <span class="material-icons-sharp section-icon">grading</span>
      <h2>Commandes</h2>
    </div>

    <p class="section-intro">
      Le module Commandes permet de créer des bons de commande pour les clients,
      d'ajouter plusieurs lignes de produits, de consulter le panier, d'imprimer le bon
      et de l'envoyer en caisse pour encaissement.
    </p>

    <!-- ===== CRÉER UNE COMMANDE ===== -->
    <h3 class="sub-title">Créer une commande</h3>
    <div class="table-wrapper">
      <table>
        <thead>
          <tr><th>Champ</th><th>Type</th><th>Obligatoire</th><th>Ce qu'il faut saisir</th></tr>
        </thead>
        <tbody>
          <tr><td><strong>Client</strong></td><td>Liste déroulante (autocomplete)</td><td><span class="required-badge badge-yes">Oui</span></td><td>Sélectionnez le client qui passe commande.</td></tr>
          <tr><td><strong>Observation</strong></td><td>Texte</td><td><span class="required-badge badge-no">Non</span></td><td>Remarques générales sur la commande.</td></tr>
        </tbody>
      </table>
    </div>

    <h4 class="sub-title">Lignes de produits (dynamiques)</h4>
    <p class="section-text">
      Chaque commande contient une ou plusieurs lignes de produits. Pour chaque ligne :
    </p>
    <div class="table-wrapper">
      <table>
        <thead>
          <tr><th>Champ</th><th>Type</th><th>Obligatoire</th><th>Ce qu'il faut saisir</th></tr>
        </thead>
        <tbody>
          <tr><td><strong>Produit</strong></td><td>Liste déroulante</td><td><span class="required-badge badge-yes">Oui</span></td><td>Le produit commandé.</td></tr>
          <tr><td><strong>Quantité disponible</strong></td><td>Nombre (désactivé)</td><td>—</td><td>Affiché automatiquement après sélection du produit.</td></tr>
          <tr><td><strong>Prix unitaire</strong></td><td>Nombre (désactivé)</td><td>—</td><td>Affiché automatiquement (défini dans le paramétrage).</td></tr>
          <tr><td><strong>Quantité</strong></td><td>Nombre</td><td><span class="required-badge badge-yes">Oui</span></td><td>Quantité commandée par le client.</td></tr>
        </tbody>
      </table>
    </div>

    <h4 class="sub-title">Procédure : Créer un bon de commande</h4>
    <div class="steps">
      <div class="step-item">
        <div class="step-num">1</div>
        <p>Ouvrez le module <strong>Commandes</strong> depuis le menu latéral.</p>
      </div>
      <div class="step-item">
        <div class="step-num">2</div>
        <p>Cliquez sur <span class="btn-ref">Ajouter</span>.</p>
      </div>
      <div class="step-item">
        <div class="step-num">3</div>
        <p>Sélectionnez le <em>Client</em> dans la liste déroulante (tapez les premières lettres pour filtrer).</p>
      </div>
      <div class="step-item">
        <div class="step-num">4</div>
        <p>Dans la première ligne de produit, sélectionnez le <em>Produit</em>. La <em>Quantité disponible</em> et le <em>Prix unitaire</em> s'affichent automatiquement.</p>
      </div>
      <div class="step-item">
        <div class="step-num">5</div>
        <p>Saisissez la <em>Quantité</em> souhaitée.</p>
      </div>
      <div class="step-item">
        <div class="step-num">6</div>
        <p>Pour ajouter un autre produit, cliquez sur le bouton <span class="btn-ref">+</span>. Une nouvelle ligne apparaît. Répétez les étapes 4-5.</p>
      </div>
      <div class="step-item">
        <div class="step-num">7</div>
        <p>Pour retirer une ligne, cliquez sur le bouton <span class="btn-ref">−</span> à côté de la ligne concernée.</p>
      </div>
      <div class="step-item">
        <div class="step-num">8</div>
        <p>Ajoutez une <em>Observation</em> si nécessaire.</p>
      </div>
      <div class="step-item">
        <div class="step-num">9</div>
        <p>Cliquez sur <span class="btn-ref">Soumettre</span>. Le bon de commande est créé.</p>
      </div>
    </div>

    <!-- ===== DÉTAILS DU PANIER ===== -->
    <h3 class="sub-title">Consulter et gérer un bon de commande</h3>
    <p class="section-text">
      Cliquez sur un bon existant dans le tableau pour voir les détails.
      Le formulaire affiche les informations suivantes (non modifiables) :
    </p>
    <div class="table-wrapper">
      <table>
        <thead>
          <tr><th>Information</th><th>Description</th></tr>
        </thead>
        <tbody>
          <tr><td><strong>Client</strong></td><td>Nom du client (affiché automatiquement).</td></tr>
          <tr><td><strong>Total</strong></td><td>Montant total de la commande (calculé automatiquement).</td></tr>
          <tr><td><strong>Date de commande</strong></td><td>Date à laquelle le bon a été créé.</td></tr>
          <tr><td><strong>Observation</strong></td><td>Remarques saisies lors de la création.</td></tr>
          <tr><td><strong>Tableau des produits</strong></td><td>Liste de tous les produits commandés avec quantités et prix.</td></tr>
        </tbody>
      </table>
    </div>

    <h4 class="sub-title">Actions disponibles</h4>
    <div class="table-wrapper">
      <table>
        <thead>
          <tr><th>Bouton</th><th>Action</th></tr>
        </thead>
        <tbody>
          <tr><td><span class="btn-ref">Ajouter plus de produits</span></td><td>Rouvre le formulaire pour ajouter de nouvelles lignes au bon existant.</td></tr>
          <tr><td><span class="btn-ref">Imprimer le bon de commande</span></td><td>Génère un PDF du bon de commande pour impression ou envoi.</td></tr>
          <tr><td><span class="btn-ref">Envoyer à la caisse</span></td><td>Transfère le bon en caisse pour que le client puisse payer.</td></tr>
        </tbody>
      </table>
    </div>

    <h4 class="sub-title">Procédure : Envoyer à la caisse</h4>
    <div class="steps">
      <div class="step-item">
        <div class="step-num">1</div>
        <p>Ouvrez le bon de commande en cliquant dessus dans le tableau.</p>
      </div>
      <div class="step-item">
        <div class="step-num">2</div>
        <p>Vérifiez les produits et le total.</p>
      </div>
      <div class="step-item">
        <div class="step-num">3</div>
        <p>Cliquez sur <span class="btn-ref">Envoyer à la caisse</span>. Le bon passe en statut « Envoyé en caisse » et apparaît dans le module Caisse.</p>
      </div>
    </div>

    <div class="info-card warning">
      <div class="card-title">
        <span class="material-icons-sharp" style="font-size:16px;color:#ff9800">warning</span>
        Attention
      </div>
      <p>
        Une fois envoyé à la caisse, le bon ne peut plus être modifié. Assurez-vous que tous les produits et quantités sont corrects avant l'envoi.
      </p>
    </div>
  </div>
</template>
<script>
export default { name: "DocCommandes" };
</script>
